import React from "react";
import styled from "styled-components";
import { X, Plus } from "lucide-react";

const Container = styled.div`
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  padding: 0.75rem;
  margin: 0.5rem 0;
  background: var(--accent, #f8fafc);
`;

const Header = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--foreground);
  margin-bottom: 0.5rem;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.375rem;
`;

const OptionInput = styled.input`
  flex: 1;
  padding: 0.4375rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  background: var(--card);
  color: var(--foreground);
  outline: none;

  &:focus {
    border-color: var(--primary, #3b82f6);
  }
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: var(--muted-foreground, #94a3b8);
  cursor: pointer;
  padding: 0.125rem;
  display: flex;

  &:hover {
    color: var(--destructive, #ef4444);
  }
`;

const AddBtn = styled.button`
  background: none;
  border: none;
  color: var(--primary, #3b82f6);
  font-size: 0.8125rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0;

  &:hover {
    text-decoration: underline;
  }
`;

const ExpiryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: var(--muted-foreground, #64748b);
`;

const ExpiryInput = styled.input`
  padding: 0.3125rem 0.5rem;
  border: 1px solid var(--border);
  border-radius: 0.375rem;
  font-size: 0.75rem;
  background: var(--card);
  color: var(--foreground);
`;

// Options carry their own id rather than being identified by array position.
// Position is not a stable identity once a row can be removed: deleting the first
// of three options shifts the survivors up, so a position-keyed list makes React
// reuse the DOM input that belonged to the deleted row. The visible symptom is a
// row's text appearing to jump to its neighbour, and focus landing in the wrong
// box mid-edit. The id is internal only -- `emitChange` still hands the parent a
// plain array of strings, which is the contract create-thread-modal expects.
let nextOptionId = 0;
const blankOption = () => {
  nextOptionId += 1;
  return { id: `option-${nextOptionId}`, text: "" };
};

const PollCreator = (props) => {
  const { onPollDataChange } = props;
  const [options, setOptions] = React.useState(() => [
    blankOption(),
    blankOption(),
  ]);
  const [expiresAt, setExpiresAt] = React.useState("");

  const emitChange = (opts, exp) => {
    onPollDataChange({
      options: opts.map((o) => o.text),
      expires_at: exp || null,
    });
  };

  const handleOptionChange = (id, value) => {
    const next = options.map((o) => (o.id === id ? { ...o, text: value } : o));
    setOptions(next);
    emitChange(next, expiresAt);
  };

  const addOption = () => {
    if (options.length >= 4) return;
    const next = [...options, blankOption()];
    setOptions(next);
    emitChange(next, expiresAt);
  };

  const removeOption = (id) => {
    if (options.length <= 2) return;
    const next = options.filter((o) => o.id !== id);
    setOptions(next);
    emitChange(next, expiresAt);
  };

  const handleExpiryChange = (e) => {
    setExpiresAt(e.target.value);
    emitChange(options, e.target.value);
  };

  const now = new Date();
  const minDatetime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}T${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

  return (
    <Container>
      <Header>Poll Options</Header>
      {options.map((opt, i) => (
        <OptionRow key={opt.id}>
          <OptionInput
            placeholder={`Option ${i + 1}`}
            value={opt.text}
            onChange={(e) => handleOptionChange(opt.id, e.target.value)}
            maxLength={100}
          />
          {options.length > 2 && (
            <RemoveBtn
              onClick={() => removeOption(opt.id)}
              title="Remove option"
            >
              <X size={14} />
            </RemoveBtn>
          )}
        </OptionRow>
      ))}
      {options.length < 4 && (
        <AddBtn onClick={addOption}>
          <Plus size={14} /> Add option
        </AddBtn>
      )}
      <ExpiryRow>
        <span>Expires:</span>
        <ExpiryInput
          type="datetime-local"
          value={expiresAt}
          onChange={handleExpiryChange}
          min={minDatetime}
        />
      </ExpiryRow>
    </Container>
  );
};

export default PollCreator;
